/**
 * observeMarkRegistry.js
 *
 * Shared per-mark observer registry for Y.Map("referenceMarks").
 *
 * Problem: Each hook (useYReferences, useClaimAnalysis, useSuggestedReferences)
 * attaches its own `observeDeep` handler on the same root Y.Map. For N active
 * ReferenceMarker instances this means 3·N handlers — all doing the same
 * path-filtering work.
 *
 * Solution: A ref-counted registry that creates exactly ONE `observeDeep`
 * handler per (yReferenceMarks, referenceMarkId) pair. Multiple subscribers
 * for the same mark share that single handler.
 *
 * Usage:
 *   const unsub = subscribeToMark(yReferenceMarks, markId, myCallback);
 *   // ... later
 *   unsub();
 */

// Registry key: we use the Y.Map instance identity + markId
// WeakMap<Y.Map, Map<referenceMarkId, Entry>>
const registry = new WeakMap();

/**
 * @typedef {Object} MarkEntry
 * @property {Set<Function>} subscribers
 * @property {Function}      handler       The single observeDeep handler
 */

/**
 * Subscribe to changes within a single reference mark's subtree.
 *
 * The first subscriber for a (yReferenceMarks, markId) pair creates one
 * `observeDeep` handler. Subsequent subscribers piggyback on it.
 * When the last subscriber unsubscribes, the handler is removed.
 *
 * @param {import('yjs').Map} yReferenceMarks  Root Y.Map("referenceMarks")
 * @param {string}            referenceMarkId  UID of the mark to observe
 * @param {Function}          callback         Called on relevant changes
 * @returns {Function}        Unsubscribe function (idempotent)
 */
export function subscribeToMark(yReferenceMarks, referenceMarkId, callback) {
    if (!yReferenceMarks || !referenceMarkId || typeof callback !== 'function') {
        return () => {
        };
    }

    // Get or create the per-Y.Map map
    let markMap = registry.get(yReferenceMarks);
    if (!markMap) {
        markMap = new Map();
        registry.set(yReferenceMarks, markMap);
    }

    // Get or create the entry for this specific markId
    let entry = markMap.get(referenceMarkId);
    if (!entry) {
        // Create the single shared handler
        const handler = (events) => {
            const relevant = events.some((event) =>
                // Root-level key change (mark added/removed)
                (event.path.length === 0 && event.keysChanged && event.keysChanged.has(referenceMarkId))
                // Subtree change under this mark
                || (event.path.length > 0 && event.path[0] === referenceMarkId)
            );
            if (relevant) {
                // Snapshot subscribers before iterating — a subscriber might
                // unsubscribe during the callback
                const subs = [...entry.subscribers];
                subs.forEach((cb) => {
                    try {
                        cb(events);
                    } catch (err) {
                        console.error('[observeMarkRegistry] subscriber error:', err);
                    }
                });
            }
        };

        entry = {subscribers: new Set(), handler};
        markMap.set(referenceMarkId, entry);
        yReferenceMarks.observeDeep(handler);
    }

    entry.subscribers.add(callback);

    // Return idempotent unsubscribe
    let unsubscribed = false;
    return () => {
        if (unsubscribed) return;
        unsubscribed = true;

        entry.subscribers.delete(callback);

        // If no more subscribers, tear down the shared handler
        if (entry.subscribers.size === 0) {
            yReferenceMarks.unobserveDeep(entry.handler);
            markMap.delete(referenceMarkId);
            if (markMap.size === 0) {
                registry.delete(yReferenceMarks);
            }
        }
    };
}

